import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Plus, Pencil, Trash2, Download, Upload } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PageHeader } from "../components/Bits";
import { CustomerDetailDialog } from "../components/CustomerDetailDialog";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, money, downloadFile } from "../lib/api";

const EMPTY = { name: "", phone: "", address: "", tax_office: "", tax_no: "" };
const FIELDS = [["name", "Unvan / Ad *"], ["phone", "Telefon"], ["tax_office", "Vergi Dairesi"], ["tax_no", "Vergi No"], ["address", "Adres"]];

const CustomerDialog = ({ open, onOpenChange, customer, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => setF(customer ? { ...EMPTY, ...customer } : EMPTY), [customer, open]);
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = Object.fromEntries(FIELDS.map(([k]) => [k, f[k] || ""]));
      if (customer) await api.put(`/customers/${customer.id}`, body); else await api.post("/customers", body);
      toast.success("Müşteri kaydedildi");
      onSaved(); onOpenChange(false);
    } catch (ex) { toast.error(errMsg(ex)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="customer-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{customer ? "Müşteriyi Düzenle" : "Yeni Müşteri"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          {FIELDS.map(([k, l]) => (
            <div key={k} className={k === "name" || k === "address" ? "col-span-2" : ""}><Label className="text-xs">{l}</Label>
              <Input data-testid={`customer-form-${k}-input`} required={k === "name"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" /></div>
          ))}
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Vazgeç</Button>
            <Button data-testid="customer-form-save-button" className="bg-slate-950 hover:bg-blue-600">Kaydet</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function CustomersPage() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [bal, setBal] = useState({ balances: {}, total: 0, debtors: 0 });
  const load = () => api.get("/customers").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  const loadBal = () => api.get("/customers-balances").then((r) => setBal(r.data)).catch(() => {});
  useEffect(() => { load(); loadBal(); }, []);
  const shown = useMemo(() => {
    const s = q.toLocaleLowerCase("tr");
    return list.filter((c) => !s || [c.name, c.phone, c.tax_no, c.address].some((v) => (v || "").toLocaleLowerCase("tr").includes(s)));
  }, [list, q]);
  const canEdit = (c) => user.role === "admin" || c.created_by === user.id;
  const del = async (c) => {
    if (!window.confirm(`"${c.name}" silinsin mi?`)) return;
    try { await api.delete(`/customers/${c.id}`); toast.success("Müşteri silindi"); load(); loadBal(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Müşteriler" kicker={`${list.length} kayıtlı müşteri`}>
        {user.role === "admin" && <>
          <Button data-testid="cari-backup-export-button" variant="outline" onClick={() => downloadFile("/backup/cari", "cari_yedek.xlsx").catch((e) => toast.error(errMsg(e)))}><Download className="mr-2 h-4 w-4" />Cari Yedeği (Excel)</Button>
          <label className="inline-flex h-10 cursor-pointer items-center rounded-md border border-slate-200 bg-white px-4 text-sm font-medium transition-colors hover:bg-slate-100" data-testid="cari-backup-import-label">
            <Upload className="mr-2 h-4 w-4 text-emerald-600" />Excel'den Cari Aktar
            <input data-testid="cari-backup-import-input" type="file" accept=".xlsx" className="hidden" onChange={async (e) => {
              const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
              const fd = new FormData(); fd.append("file", f);
              try {
                const { data } = await api.post("/backup/cari/import", fd);
                toast.success(`Müşteri: ${data.customers_created} yeni, ${data.customers_updated} güncellendi · Tedarikçi: ${data.suppliers_created} yeni, ${data.suppliers_updated} güncellendi`);
                data.errors.forEach((x) => toast.error(x));
                load(); loadBal();
              } catch (ex) { toast.error(errMsg(ex)); }
            }} />
          </label>
        </>}
        <Button data-testid="customers-add-button" className="bg-slate-950 hover:bg-blue-600" onClick={() => { setEdit(null); setOpen(true); }}><Plus className="mr-2 h-4 w-4" />Yeni Müşteri</Button>
      </PageHeader>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3 fade-up">
        <div className="rounded-xl border border-slate-950 bg-slate-950 p-5 text-white shadow-sm sm:col-span-2">
          <p className="font-mono text-[11px] uppercase tracking-wider text-cyan-300">Toplam Müşteri Carisi</p>
          <p className="mt-2 font-display text-3xl font-bold" data-testid="customers-total-balance">{money(bal.total)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Borçlu Müşteri</p>
          <p className="mt-2 font-display text-3xl font-bold" data-testid="customers-debtor-count">{bal.debtors} <span className="text-base font-medium text-slate-400">/ {list.length}</span></p>
        </div>
      </div>
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid="customers-search-input" placeholder="Unvan, telefon, vergi no ara..." value={q} onChange={(e) => setQ(e.target.value)} className="h-11 bg-white pl-10" />
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left font-mono text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="p-4">Unvan</th><th className="p-4">Telefon</th><th className="p-4">Vergi D. / No</th><th className="p-4">Adres</th><th className="p-4">Plasiyer</th><th className="p-4 text-right">Bakiye</th><th className="p-4" /></tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={7} className="p-12 text-center text-slate-500" data-testid="customers-empty">Müşteri bulunamadı</td></tr>}
            {shown.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`} onClick={() => setDetail(c)} className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-blue-50/50">
                <td className="p-4 font-semibold">{c.name}<span className={`block text-xs sm:hidden ${(bal.balances[c.id] || 0) > 0.005 ? "text-rose-700" : "text-slate-400"}`} data-testid={`customer-balance-mobile-${c.id}`}>Bakiye: {money(bal.balances[c.id] || 0)}</span></td><td className="p-4 whitespace-nowrap text-slate-600">{c.phone || "-"}</td>
                <td className="p-4 text-slate-600">{[c.tax_office, c.tax_no].filter(Boolean).join(" / ") || "-"}</td>
                <td className="p-4 max-w-xs truncate text-slate-600">{c.address || "-"}</td><td className="p-4"><span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700" data-testid={`customer-plasiyer-${c.id}`}>{c.created_by_name || "-"}</span></td>
                <td className="p-4 text-right whitespace-nowrap font-display font-bold" data-testid={`customer-balance-${c.id}`}>
                  <span className={(bal.balances[c.id] || 0) > 0.005 ? "text-rose-700" : "text-slate-400"}>{money(bal.balances[c.id] || 0)}</span>
                  {c.credit_limit > 0 && <p className="text-[10px] font-normal text-slate-500">Limit {money(c.credit_limit)}</p>}
                </td>
                <td className="p-4 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>{canEdit(c) && <>
                  <button data-testid={`customer-edit-${c.id}`} onClick={() => { setEdit(c); setOpen(true); }} className="mr-1 inline-grid h-8 w-8 place-items-center rounded-lg border border-slate-200 hover:bg-slate-100"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid={`customer-delete-${c.id}`} onClick={() => del(c)} className="inline-grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button>
                </>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CustomerDetailDialog customer={detail} onClose={() => { setDetail(null); loadBal(); load(); }} onChanged={loadBal} />
      <CustomerDialog open={open} onOpenChange={setOpen} customer={edit} onSaved={load} />
    </div>
  );
}
